import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Shield, Eye, EyeOff, CheckCircle, AlertTriangle, Search } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';

const ActivateAccount: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Validation du mot de passe
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
    special: false
  });

  useEffect(() => {
    if (!token) {
      setError('Token d\'activation manquant');
      return;
    }
  }, [token]);

  useEffect(() => {
    // Valider le mot de passe en temps réel
    setPasswordValidation({
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[@$!%*?&]/.test(password)
    });
  }, [password]);

  const isPasswordValid = Object.values(passwordValidation).every(Boolean);
  const doPasswordsMatch = password === confirmPassword && password.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      setError('Token d\'activation manquant');
      return;
    }

    if (!isPasswordValid) {
      setError('Le mot de passe ne respecte pas tous les critères');
      return;
    }

    if (!doPasswordsMatch) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(API_ENDPOINTS.ADMIN_ACTIVATE_ACCOUNT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          password
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(data.error || 'Erreur lors de l\'activation du compte');
      }

    } catch (error) {
      console.error('Activation error:', error);
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-osint-dark p-4">
        <div className="w-full max-w-md">
          <div className="osint-card rounded-2xl p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-600 rounded-full mb-6">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            
            <h1 className="text-2xl font-bold text-white mb-4">
              Compte activé avec succès !
            </h1>
            
            <p className="text-gray-300 mb-6">
              Votre compte a été créé et activé. Vous allez être redirigé vers la page de connexion.
            </p>
            
            <div className="flex items-center justify-center space-x-2 text-osint-orange">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-osint-orange"></div>
              <span>Redirection en cours...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-osint-dark p-4">
      <div className="w-full max-w-md">
        {/* Logo et titre */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-osint-orange rounded-full mb-4">
            <Search className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">OSINT Dashboard</h1>
          <p className="text-osint-light-gray">Activation de compte</p>
        </div>

        {/* Formulaire d'activation */}
        <div className="osint-card rounded-2xl p-8">
          <div className="flex items-center mb-6">
            <Shield className="w-6 h-6 text-osint-orange mr-3" />
            <h2 className="text-xl font-semibold text-white">Créer votre mot de passe</h2>
          </div>

          {error && (
            <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 flex items-center space-x-2 mb-6">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Mot de passe */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Mot de passe
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="osint-input w-full px-4 py-3 rounded-lg text-white placeholder-gray-400 focus:outline-none pr-12"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Critères du mot de passe */}
            {password && (
              <div className="bg-osint-light-gray rounded-lg p-4">
                <h4 className="text-sm font-medium text-white mb-3">Critères du mot de passe :</h4>
                <div className="space-y-2">
                  {[
                    { key: 'length', label: 'Au moins 8 caractères' },
                    { key: 'lowercase', label: 'Au moins une minuscule (a-z)' },
                    { key: 'uppercase', label: 'Au moins une majuscule (A-Z)' },
                    { key: 'number', label: 'Au moins un chiffre (0-9)' },
                    { key: 'special', label: 'Au moins un caractère spécial (@$!%*?&)' }
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center space-x-2">
                      <CheckCircle 
                        className={`w-4 h-4 ${
                          passwordValidation[key as keyof typeof passwordValidation]
                            ? 'text-green-400' 
                            : 'text-gray-400'
                        }`} 
                      />
                      <span className={`text-sm ${
                        passwordValidation[key as keyof typeof passwordValidation]
                          ? 'text-green-400' 
                          : 'text-gray-400'
                      }`}>
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Confirmation du mot de passe */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                Confirmer le mot de passe
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="osint-input w-full px-4 py-3 rounded-lg text-white placeholder-gray-400 focus:outline-none pr-12"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {confirmPassword && (
                <div className="mt-2 flex items-center space-x-2">
                  <CheckCircle 
                    className={`w-4 h-4 ${
                      doPasswordsMatch ? 'text-green-400' : 'text-red-400'
                    }`} 
                  />
                  <span className={`text-sm ${
                    doPasswordsMatch ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {doPasswordsMatch ? 'Les mots de passe correspondent' : 'Les mots de passe ne correspondent pas'}
                  </span>
                </div>
              )}
            </div>

            {/* Bouton d'activation */}
            <button
              type="submit"
              disabled={loading || !isPasswordValid || !doPasswordsMatch}
              className="osint-button w-full py-3 px-4 rounded-lg text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Activation en cours...
                </div>
              ) : (
                'Activer mon compte'
              )}
            </button>
          </form>

          {/* Informations de sécurité */}
          <div className="mt-6 p-4 bg-blue-900/20 border border-blue-500/50 rounded-lg">
            <div className="flex items-start space-x-2">
              <Shield className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-blue-400 text-sm font-medium mb-1">Sécurité</p>
                <p className="text-blue-300 text-xs">
                  Votre mot de passe sera chiffré et stocké de manière sécurisée. 
                  Nous vous recommandons d'activer l'authentification à deux facteurs une fois connecté.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-500">
            Système de sécurité OSINT v1.0
          </p>
        </div>
      </div>
    </div>
  );
};

export default ActivateAccount;